import React, {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import ReactRouterPrompt from 'react-router-prompt';
import {
  useDispatch,
  useSelector
} from 'react-redux';
import {
  Form,
  FormSpy,
} from 'react-final-form';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { makeValidate } from 'mui-rff';
import * as Yup from 'yup';
import slugify from 'slugify/slugify';
import {
  Container,
  CircularProgress,
  Fab,
  Grid,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';

import { SLUGIFY_REGEX, URL_REGEXP } from 'src/utils/consts';
import {
  clearAdsData,
  createAd,
  deleteAd,
  fetchAdDetails,
  updateAd,
} from 'src/store/actions/ads';
import adsSelectors from 'src/store/selectors/ads';

import AlertDialog from 'src/components/AlertDialog';
import Loading from 'src/components/Loading';
import Page from 'src/components/Page';
import AdDetails from './AdDetails';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(12),
    paddingTop: theme.spacing(3)
  },
  deleteButton: {
    background: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      background: theme.palette.error.dark,
    }
  },
  floatingActionButton: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(4),
  },
}));

// Validation
const schema = Yup.object().shape({
  title: Yup.string()
    .required('Pole jest wymagane'),
  url: Yup.string()
    .required('Pole jest wymagane')
    .matches(URL_REGEXP, 'Niepoprawny adres URL'),
  type: Yup.string()
    .required('Pole jest wymagane'),
  expiryDate: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Nieprawidłowa data'),
  expiryViews: Yup.number()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Nieprawidłowa wartość'),
  slug: Yup.string()
    .required('Pole jest wymagane'),
  status: Yup.string()
    .required('Pole jest wymagane'),
});

const AdDetailView = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const classes = useStyles();
  const deleteAlertRef = useRef();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0); // eslint-disable-line

  const ad = useSelector((state) => adsSelectors.getAdDetails(state));
  const isFetching = useSelector((state) => adsSelectors.getAdDetailsIsFetching(state));

  const [imageFile, setImageFile] = useState(null);

  const language = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('language') || 'pl';
  }, [location.search]);

  useEffect(() => {
    if (action === 'edit') {
      const fetchData = async () => {
        await dispatch(fetchAdDetails(language, id));
      };

      fetchData()
        .catch(() => {
          enqueueSnackbar('Wystąpił problem podczas pobierania danych', { variant: 'error' });
        });
    } else {
      // console.log(validate);
    }

    return () => {
      dispatch(clearAdsData());
    };
  }, [action, dispatch, enqueueSnackbar, id, language]);

  const createSlug = (title) => {
    return slugify(title, {
      lower: true,
      remove: SLUGIFY_REGEX,
    });
  };

  const getSubmitIcon = (status) => {
    if (action === 'add') {
      return <AddIcon />;
    }

    if (status === 'deleted') {
      return <DeleteIcon />;
    }

    return <SaveIcon />;
  };

  const getSubmitLabel = (status) => {
    if (action === 'add') {
      return 'Dodaj';
    }

    if (status === 'deleted') {
      return 'Usuń';
    }

    return 'Zapisz zmiany';
  };

  const onSubmit = async (values) => {
    if (values.status === 'deleted') {
      deleteAlertRef.current.open();
      return false;
    }

    values.slug = createSlug(values.slug);

    try {
      const formData = new FormData();

      Object.entries(values).forEach((entry) => {
        const [key, value] = entry;
        formData.append(key, value);
      });

      if (imageFile !== null) {
        formData.append('image', imageFile ? imageFile[0].file : false);
      }

      if (action === 'edit') {
        await dispatch(updateAd(id, formData));
        enqueueSnackbar('Zmiany zostały zapisane', { variant: 'success' });
      } else {
        await dispatch(createAd(formData));
        enqueueSnackbar('Dodano nową kategorię', { variant: 'success' });

        setTimeout(() => {
          navigate('/ads');
        });
      }

      return true;
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Wystąpił błąd podczas wysyłania danych', { variant: 'error' });

      return error;
    }
  };

  const onDelete = async () => {
    try {
      await dispatch(deleteAd(id));
      enqueueSnackbar('Reklama została usunięta', { variant: 'success' });

      setTimeout(() => {
        navigate('/ads');
      });

      return true;
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Wystąpił błąd podczas wysyłania danych', { variant: 'error' });

      return error;
    }
  };

  return (
    <Form
      initialValues={
        ad ? {
          title: ad.title ?? '',
          url: ad.url ?? '',
          type: ad.type,
          slug: ad.slug ?? '',
          expiryDate: ad.expiryDate ? moment(ad.expiryDate).format('yyyy-MM-DD') : '',
          expiryViews: ad.expiryViews ?? '',
          status: ad.status,
        } : {
          status: 'active',
        }
      }
      keepDirtyOnReinitialize
      mutators={{
        setSlug: (args, state, utils) => {
          const slug = createSlug(args[0]);
          if ((action === 'add' || (action === 'edit' && !ad.title)) && !state.fields.slug.modified) {
            utils.changeValue(state, 'slug', () => slug);
          }
        },
        setExpiryDate: (args, state, utils) => {
          const date = args[0];
          utils.changeValue(state, 'expiryDate', () => date);
        },
      }}
      onSubmit={onSubmit}
      subscription={{
        dirty: true,
        submitSucceeded: true,
      }}
      validate={makeValidate(schema)}
      render={({
        dirty,
        form,
        handleSubmit,
        submitSucceeded,
      }) => (
        <Page
          appBar={{
            title: action === 'edit' ? 'Edycja reklamy' : 'Dodaj nową reklamę',
            hasBackButton: true,
            backTo: '/ads',
          }}
          className={classes.root}
          title={
            action === 'edit'
              ? `Edycja reklamy${ad ? `: ${ad.title ?? ''} [ID: #${ad.id}]` : ''}`
              : 'Dodaj nową reklamę'
          }
        >
          {(action === 'add' || ad) ? (
            <form onSubmit={handleSubmit} noValidate>
              <Container maxWidth="lg">
                <Grid
                  container
                  spacing={3}
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <AdDetails
                      data={ad}
                      imageFile={imageFile}
                      setImageFile={setImageFile}
                      setExpiryDate={form.mutators.setExpiryDate}
                      setSlug={form.mutators.setSlug}
                    />
                  </Grid>
                </Grid>
                <FormSpy
                  subscription={{
                    submitting: true,
                    values: true,
                  }}
                >
                  {({ submitting, values }) => (
                    <Tooltip
                      placement="left"
                      title={getSubmitLabel(values.status)}
                    >
                      <Fab
                        aria-label="save"
                        className={clsx(
                          classes.floatingActionButton,
                          values.status === 'deleted' && classes.deleteButton,
                        )}
                        color="secondary"
                        disabled={submitting}
                        type="submit"
                      >
                        {!submitting
                          ? getSubmitIcon(values.status)
                          : <CircularProgress color="secondary" size={26} />}
                      </Fab>
                    </Tooltip>
                  )}
                </FormSpy>
              </Container>
              <ReactRouterPrompt when={dirty && !submitSucceeded}>
                {({ isActive, onConfirm, onCancel }) => (
                  <AlertDialog
                    title="Nie zapisano zmian"
                    message="Czy pewno chcesz opuścić stronę bez zapisywania zmian?"
                    isOpen={isActive}
                    actions={[
                      {
                        label: 'Wyjdź bez zapisywania',
                        onClick: onConfirm,
                      },
                      {
                        autoFocus: true,
                        label: 'Wróć',
                        variant: 'contained',
                        onClick: onCancel,
                      },
                    ]}
                  />
                )}
              </ReactRouterPrompt>
              <AlertDialog
                title="Czy na pewno chcesz usunąć tą reklamę?"
                ref={deleteAlertRef}
                actions={[
                  {
                    label: 'Wróć',
                  },
                  {
                    className: classes.deleteButton,
                    disabled: isFetching,
                    label: 'Usuń',
                    loading: isFetching,
                    onClick: () => {
                      onDelete();
                      deleteAlertRef.current.close();
                    },
                  },
                ]}
              />
            </form>
          ) : (
            <Loading />
          )}
        </Page>
      )}
    />
  );
};

AdDetailView.propTypes = {
  action: PropTypes.string,
};

AdDetailView.defaultProps = {
  action: 'add',
};

export default AdDetailView;
